import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { wholesale_forecast_by_account_node_products_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import type { ProductResult } from "./FlatProductSearch";

interface Props {
  leafId: string | null;
  query: string;
  existingExternalIds: Set<string>;
  onPick: (product: ProductResult) => void;
}

const DEBOUNCE_MS = 300;

// Right pane of the add-product picker: the selected leaf's products, or —
// with a query and no leaf — an org-wide search, so a known short_name can
// be typed straight away without drilling. Resolution happens server-side
// with the same logic Bottom Up uses. The list is capped at 50 rows; the
// dialog-level filter box re-queries via the `query` prop.
export default function LeafProductList({ leafId, query, existingExternalIds, onPick }: Props) {
  const market = useMarket();
  const [total, setTotal] = useState(0);
  const [results, setResults] = useState<ProductResult[]>([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  const active = leafId != null || query.trim() !== "";

  useEffect(() => {
    if (!active) return;
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ q: query });
      if (leafId) params.set("node_id", leafId);
      const url = `${wholesale_forecast_by_account_node_products_path(market.slug)}?${params}`;
      fetch(url, { headers: { Accept: "application/json" } })
        .then((res) => res.json())
        .then((json: { total: number; products: ProductResult[] }) => {
          if (id !== requestId.current) return;
          setTotal(json.total);
          setResults(json.products);
        })
        .finally(() => {
          if (id === requestId.current) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [active, leafId, query, market.slug]);

  const capped = total > results.length;

  // Fixed h-6 header mirrors the drill-down's breadcrumb row so both panes'
  // bordered boxes share the same top edge.
  const headerText = !active || loading
    ? ""
    : capped
      ? `Showing ${results.length} of ${total.toLocaleString("en-US")} — type to filter.`
      : `${total.toLocaleString("en-US")} ${total === 1 ? "product" : "products"}`;

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex h-6 shrink-0 items-center text-xs text-muted-foreground">{headerText}</div>

      {!active ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-md border px-6 text-center text-xs text-muted-foreground">
          Type to search all products, or drill into a node on the left.
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
          {loading && (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">Loading…</div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-muted-foreground">
              No matching products.
            </div>
          )}
          {!loading &&
            results.map((p) => {
              const added = existingExternalIds.has(p.external_id);
              return (
                <Button
                  key={p.external_id}
                  type="button"
                  variant="ghost"
                  disabled={added}
                  onClick={() => onPick(p)}
                  className="h-auto w-full items-start justify-between gap-3 rounded-none border-b px-3 py-2 text-left font-normal last:border-b-0"
                >
                  <span className="min-w-0">
                    <span className="block whitespace-normal text-foreground">{p.name}</span>
                    {p.short_name && (
                      <span className="block whitespace-normal text-xs text-muted-foreground">{p.short_name}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {added ? "already added" : `#${p.external_id}`}
                  </span>
                </Button>
              );
            })}
        </div>
      )}
    </div>
  );
}
